import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Row,
} from 'react-bootstrap';

import {
  ContestantRegistrationDetailsDTO,
  EventJudgeAgeGroupDTO,
  EventJudgeContestantDTO,
  EventJudgeDivisionDTO,
} from 'API/Generated/data-contracts';
import { RegistrationInvoice } from 'API/Generated/data-contracts';
import ContestantDetailsCard from './ContestantDetailsCard';

type ContestantBrowserHeader = {
  title: string;
  subtitle?: string;
  backLabel?: string;
  contestantCount?: number;
  backAction?: () => void;
};

type DivisionContestantBrowserProps = {
  divisions: EventJudgeDivisionDTO[];

  onHeaderChange?: (
    header: ContestantBrowserHeader,
  ) => void;

  loadContestantDetails: (
    registeredContestantId: number,
  ) => Promise<ContestantRegistrationDetailsDTO | null>;
};

const DivisionContestantBrowser = ({
  divisions,
  onHeaderChange,
  loadContestantDetails,
}: DivisionContestantBrowserProps) => {
  const [
    selectedDivision,
    setSelectedDivision,
  ] =
    React.useState<EventJudgeDivisionDTO | null>(
      null,
    );

  const [
    selectedAgeGroup,
    setSelectedAgeGroup,
  ] =
    React.useState<EventJudgeAgeGroupDTO | null>(
      null,
    );

  const [
    selectedAocId,
    setSelectedAocId,
  ] = React.useState<string>('');

  const [
    selectedContestant,
    setSelectedContestant,
  ] =
    React.useState<EventJudgeContestantDTO | null>(
      null,
    );

  const [
    searchText,
    setSearchText,
  ] = React.useState<string>('');

  const goBackToDivisions = (): void => {
  setSelectedDivision(null);
  setSelectedAgeGroup(null);
  setSelectedAocId('');
  setSelectedContestant(null);
  setSearchText('');
};

const goBackToAgeGroups =
  (): void => {
    setSelectedAgeGroup(null);
    setSelectedAocId('');
    setSelectedContestant(null);
    setSearchText('');
  };
  
  const goBackToContestants = (): void => {
  setSelectedContestant(null);
};
  // =====================================================
  // UPDATE MODAL TITLE
  // =====================================================

React.useEffect(() => {
  if (!onHeaderChange) {
    return;
  }

  // =====================================================
  // CONTESTANT DETAILS
  // =====================================================

  if (
    selectedDivision &&
    selectedAgeGroup &&
    selectedContestant
  ) {
    onHeaderChange({
      title:
        selectedContestant.contestantName ??
        'Contestant Details',

      subtitle: `#${
        selectedContestant.contestantNumber ??
        ''
      }`,

      backLabel:
        selectedAgeGroup.ageGroupName ??
        'Contestants',

      backAction:
        goBackToContestants,
    });

    return;
  }

  // =====================================================
  // CONTESTANT LIST
  // =====================================================

  if (
    selectedDivision &&
    selectedAgeGroup
  ) {
    const contestants =
      selectedAgeGroup.contestants ??
      [];

    onHeaderChange({
      title:
        selectedAgeGroup.ageGroupName ??
        'Contestants',

      subtitle:
        selectedDivision.divisionName ??
        '',

      backLabel:
        selectedDivision.divisionName ??
        'Back',

      backAction:
        goBackToAgeGroups,

      contestantCount:
        contestants.length,
    });

    return;
  }

  // =====================================================
  // AGE GROUPS
  // =====================================================

  if (selectedDivision) {
    onHeaderChange({
      title:
        selectedDivision.divisionName ??
        'Contestants',

      subtitle:
        'Select an age group',

      backLabel:
        'Divisions',

      backAction:
        goBackToDivisions,

      contestantCount:
        getDivisionContestantCount(
          selectedDivision,
        ),
    });

    return;
  }

  // =====================================================
  // DIVISIONS
  // =====================================================

  onHeaderChange({
    title: 'Contestants',
  });
}, [
  selectedDivision,
  selectedAgeGroup,
  selectedContestant,
  onHeaderChange,
]);

  // =====================================================
  // HELPERS
  // =====================================================

  const sortContestants = (
    contestants:
      | EventJudgeContestantDTO[]
      | null
      | undefined,
  ): EventJudgeContestantDTO[] => {
    return [...(contestants ?? [])].sort(
      (a, b) => {
        const aNumber = Number(
          a.contestantNumber,
        );

        const bNumber = Number(
          b.contestantNumber,
        );

        const aValid =
          Number.isFinite(aNumber);

        const bValid =
          Number.isFinite(bNumber);

        if (aValid && bValid) {
          return aNumber - bNumber;
        }

        return (
          a.contestantNumber ?? ''
        ).localeCompare(
          b.contestantNumber ?? '',
        );
      },
    );
  };

  const getContestantDisplayName = (
    contestant: EventJudgeContestantDTO,
  ): string => {
    const fullName =
      contestant.contestantName?.trim() ??
      '';

    if (!fullName) {
      return 'Unknown';
    }

    const parts = fullName
      .split(/\s+/)
      .filter(Boolean);

    const firstName =
      parts[0] ?? '';

    const lastName =
      parts.length > 1
        ? parts[parts.length - 1]
        : '';

    const lastInitial =
      lastName
        ? `${lastName.charAt(0)}.`
        : '';

    return [
      firstName,
      lastInitial,
    ]
      .filter(Boolean)
      .join(' ');
  };

  const getDivisionContestantCount = (
  division: EventJudgeDivisionDTO,
): number => {
  const ageGroups =
    division.ageGroups ?? [];

  if (ageGroups.length > 0) {
    return ageGroups.reduce(
      (
        total,
        ageGroup,
      ) =>
        total +
        (
          ageGroup.contestants
            ?.length ??
          0
        ),
      0,
    );
  }

  return (
    division.contestants
      ?.length ??
    0
  );
};



const openDivision = (
  division: EventJudgeDivisionDTO,
): void => {
  setSelectedDivision(
    division,
  );

  setSelectedAocId('');
  setSelectedContestant(
    null,
  );
  setSearchText('');

  const ageGroups =
    division.ageGroups ??
    [];

  if (
    ageGroups.length >
    0
  ) {
    setSelectedAgeGroup(
      null,
    );

    return;
  }

  // Synthetic selection for a division-only pageant.
  setSelectedAgeGroup({
    ageGroupId: undefined,
    ageGroupName:
      division.divisionName ??
      'Contestants',

    contestants:
      division.contestants ??
      [],
  });
};
  const openAgeGroup = (
    ageGroup: EventJudgeAgeGroupDTO,
  ): void => {
    setSelectedAgeGroup(ageGroup);
    setSelectedAocId('');
    setSelectedContestant(null);
    setSearchText('');
  };

  // =====================================================
  // EMPTY STATE
  // =====================================================

  if (!divisions.length) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center text-muted py-5 fs-5">
          No divisions found.
        </Card.Body>
      </Card>
    );
  }

  // =====================================================
// STEP 4
// CONTESTANT DETAILS
// =====================================================

if (
  selectedDivision &&
  selectedAgeGroup &&
  selectedContestant
) {
  return (
 <ContestantDetailsCard
  contestant={
    selectedContestant
  }
  loadDetails={
    loadContestantDetails
  }
  onBack={() => {
    setSelectedContestant(
      null,
    );
  }}
/>
  );
}

  // =====================================================
  // STEP 3
  // CONTESTANT LIST + SEARCH + AOC FILTER
  // =====================================================

  if (
    selectedDivision &&
    selectedAgeGroup
  ) {
    const contestants =
      sortContestants(
        selectedAgeGroup.contestants,
      );

    const availableAocs =
      Array.from(
        new Map(
          contestants
            .flatMap(
              (contestant) =>
                contestant
                  .areasOfCompetition ??
                [],
            )
            .map((aoc) => [
              aoc.pageantAvailableEventId,
              aoc,
            ]),
        ).values(),
      ).sort((a, b) =>
        (
          a.aocName ?? ''
        ).localeCompare(
          b.aocName ?? '',
        ),
      );

    const normalizedSearch =
      searchText
        .trim()
        .toLowerCase();

    const filteredContestants =
      contestants.filter(
        (contestant) => {
          // AOC FILTER
          const matchesAoc =
            !selectedAocId ||
            (contestant
              .areasOfCompetition
              ?.some(
                (aoc) =>
                  String(
                    aoc.pageantAvailableEventId,
                  ) ===
                  selectedAocId,
              ) ?? false);

          // NAME SEARCH
          const contestantName =
            contestant
              .contestantName
              ?.trim()
              .toLowerCase() ??
            '';

          // CONTESTANT NUMBER SEARCH
          const contestantNumber =
            String(
              contestant
                .contestantNumber ??
                '',
            ).toLowerCase();

          const matchesSearch =
            !normalizedSearch ||
            contestantName.includes(
              normalizedSearch,
            ) ||
            contestantNumber.includes(
              normalizedSearch,
            );

          return (
            matchesAoc &&
            matchesSearch
          );
        },
      );

    return (
      <>
        
      
        {/* ================================================= */}
        {/* SEARCH + AOC FILTER */}
        {/* ================================================= */}

        <Card className="border-0 bg-primary mb-4">
          <Card.Body className="p-4">
            <Row className="g-3">
              {/* SEARCH */}

              <Col
                xs={12}
                md={6}
              >
                <Form.Group>
                  <Form.Label className="fw-bold text-white">
                    Search Contestants
                  </Form.Label>

                  <Form.Control
                    size="lg"
                    type="search"
                    placeholder="Name or contestant #..."
                    value={searchText}
                    onChange={(
                      event,
                    ) => {
                      setSearchText(
                        event.target
                          .value,
                      );
                    }}
                  />
                </Form.Group>
              </Col>

              {/* AOC FILTER */}

              <Col
                xs={12}
                md={6}
              >
                <Form.Group>
                  <Form.Label className="fw-bold text-white">
                    Filter By Area of Competition
                  </Form.Label>

                  <Form.Select
                    size="lg"
                    value={
                      selectedAocId
                    }
                    onChange={(
                      event,
                    ) => {
                      setSelectedAocId(
                        event.target
                          .value,
                      );
                    }}
                  >
                    <option value="">
                      All Areas of Competition
                    </option>

                    {availableAocs.map(
                      (aoc) => (
                        <option
                          key={
                            aoc.pageantAvailableEventId
                          }
                          value={
                            aoc.pageantAvailableEventId
                          }
                        >
                          {
                            aoc.aocName
                          }
                        </option>
                      ),
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {(selectedAocId ||
              searchText) && (
              <div className="mt-3 d-flex justify-content-between align-items-center gap-3">
                <div className="text-muted fs-6">
                  Showing{' '}
                  <strong>
                    {
                      filteredContestants.length
                    }
                  </strong>{' '}
                  of{' '}
                  <strong>
                    {
                      contestants.length
                    }
                  </strong>{' '}
                  contestants
                </div>

                <Button
                  variant="link"
                  className="p-0 text-decoration-none fs-6 fw-semibold"
                  onClick={() => {
                    setSelectedAocId(
                      '',
                    );

                    setSearchText(
                      '',
                    );
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ================================================= */}
        {/* CONTESTANT LIST */}
        {/* ================================================= */}

        {filteredContestants.length >
        0 ? (
          <div className="border rounded overflow-hidden">
            {filteredContestants.map(
              (
                contestant,
                index,
              ) => {
                const enteredAocs =
                  contestant
                    .areasOfCompetition ??
                  [];

                return (
                  <button
                    key={
                      contestant
                        .registeredContestantId
                    }
                    type="button"
                    className={`w-100 border-0 bg-white text-start p-4 ${
                      index <
                      filteredContestants.length -
                        1
                        ? 'border-bottom'
                        : ''
                    }`}
                    onClick={() => {
                      setSelectedContestant(
                        contestant,
                      );
                    }}
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div className="d-flex gap-3 flex-grow-1">
                        {/* CONTESTANT NUMBER */}

                        <div
                          className="fw-bold fs-3"
                          style={{
                            minWidth:
                              '85px',
                          }}
                        >
                          #
                          {
                            contestant
                              .contestantNumber
                          }
                        </div>

                        {/* CONTESTANT INFO */}

                        <div className="flex-grow-1">
                          <div className="fw-bold fs-4">
                            {getContestantDisplayName(
                              contestant,
                            )}
                          </div>

                          {/* ENTERED AOCs */}

                          <div className="mt-2">
                            <div className="text-muted fw-bold fs-6">
                              Entered:
                            </div>

                            {enteredAocs.length >
                            0 ? (
                              <div >
                                {enteredAocs
                                  .map(
                                    (aoc) =>
                                      aoc.aocName,
                                  )
                                  .filter(
                                    Boolean,
                                  )
                                  .join(
                                    ', ',
                                  )}
                              </div>
                            ) : (
                              <div className="fs-5 text-muted">
                                None
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="fs-2 text-muted">
                        ›
                      </div>
                    </div>
                  </button>
                );
              },
            )}
          </div>
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5 fs-5">
              No contestants match
              the current filters.
            </Card.Body>
          </Card>
        )}

        {/* ================================================= */}
        {/* CONTESTANT DETAILS - NEXT STEP */}
        {/* ================================================= */}

        {selectedContestant && (
          <div className="d-none">
            {
              selectedContestant
                .registeredContestantId
            }
          </div>
        )}
      </>
    );
  }

  // =====================================================
  // STEP 2
  // SELECT AGE GROUP
  // =====================================================

  if (selectedDivision) {
    const ageGroups =
      selectedDivision.ageGroups ??
      [];

    return (
      <>
        {ageGroups.length > 0 ? (
          <Row className="g-3">
            {ageGroups.map(
              (ageGroup) => {
                const contestantCount =
                  ageGroup
                    .contestants
                    ?.length ?? 0;

                return (
                  <Col
                    key={
                      ageGroup
                        .ageGroupId
                    }
                    xs={12}
                    md={6}
                    lg={4}
                  >
                    <Card
                      role="button"
                      className="h-100 shadow-sm"
                      onClick={() => {
                        openAgeGroup(
                          ageGroup,
                        );
                      }}
                      style={{
                        cursor:
                          'pointer',
                      }}
                    >
                      <Card.Body className="p-4">
                        <div className="d-flex justify-content-between align-items-center gap-3">
                          <div>
                            <div className="fw-bold fs-4">
                              {
                                ageGroup
                                  .ageGroupName
                              }
                            </div>

                            <div className="fs-5 text-muted mt-1">
                              {
                                contestantCount
                              }{' '}
                              {contestantCount ===
                              1
                                ? 'contestant'
                                : 'contestants'}
                            </div>
                          </div>

                          <div className="fs-2 text-muted">
                            ›
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              },
            )}
          </Row>
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5 fs-5">
              No age groups found
              in this division.
            </Card.Body>
          </Card>
        )}
      </>
    );
  }

  // =====================================================
  // STEP 1
  // SELECT DIVISION
  // =====================================================

  return (
    <>
      <div className="mb-4">
        <h3 className="mb-1 fw-bold">
          Divisions
        </h3>

        <div className="fs-5 text-muted">
          Select a division.
        </div>
      </div>

      <Row className="g-3">
        {divisions.map(
          (division) => {
            const contestantCount =
              getDivisionContestantCount(
                division,
              );

            const ageGroupCount =
              division.ageGroups
                ?.length ?? 0;

            return (
              <Col
                key={
                  division.divisionId
                }
                xs={12}
                md={6}
                lg={4}
              >
                <Card
                  role="button"
                  className="h-100 shadow-sm"
                  onClick={() => {
                    openDivision(
                      division,
                    );
                  }}
                  style={{
                    cursor:
                      'pointer',
                  }}
                >
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <div>
                        <div className="fw-bold fs-4">
                          {
                            division
                              .divisionName
                          }
                        </div>

                        <div className="fs-5 text-muted mt-1">
                          {
                            contestantCount
                          }{' '}
                          {contestantCount ===
                          1
                            ? 'contestant'
                            : 'contestants'}
                        </div>

                        <div className="fs-5 text-muted">
                          {
                            ageGroupCount
                          }{' '}
                          {ageGroupCount ===
                          1
                            ? 'age group'
                            : 'age groups'}
                        </div>
                      </div>

                      <div className="fs-2 text-muted">
                        ›
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          },
        )}
      </Row>
    </>
  );
};

export default DivisionContestantBrowser;